/**
 * @module client-api-base
 * @module_directory /modules/client/base
 * @module_path hooks
 * @description Link creation hook
 */
import { useCurrentTenant } from "@qpr/client-library";
import React from "react";
import { useHistory } from "react-router-dom";
import { useAppContext } from "./../context/AppCtxProvider";
const useLink = (options?: { noPeriod?: boolean }) => {
  const history = useHistory();
  const ctx = useAppContext();
  const tenant = useCurrentTenant();
  const cb = React.useCallback(
    (params: {
      page?: string;
      meaId?: string;
      period?: string;
      model?: string;
      wf?: string;
      type?: string;
      replace?: boolean;
    }) => {
      // Determine the correct model ID to use:
      // 1. If explicitly provided in params, use it
      // 2. If meaId (element ID) contains a model ID (e.g., SC.827938453.xxx), extract and use it
      // 3. Otherwise, use the current model ID from context
      let targetModel = params.model;
      
      // CRITICAL: Only use elementId from params.meaId, NOT from ctx.elementId
      // This prevents old element IDs from being included when period changes
      // If params.meaId is explicitly provided (even if empty string), use it
      // Only fall back to ctx.elementId if params.meaId is not provided at all
      // Empty string means "explicitly exclude element ID", so don't fall back
      const elementIdToUse = params.meaId !== undefined ? (params.meaId || "") : (ctx.elementId || "");
      
      if (!targetModel) {
        // Check if meaId contains a model ID (format: SC.{modelId}.{elementId})
        // Only check params.meaId, not ctx.elementId, to avoid using stale values
        if (elementIdToUse) {
          const scMatch = elementIdToUse.match(/^SC\.(\d{10})\./);
          if (scMatch) {
            targetModel = scMatch[1]; // Use the model ID from the element ID
          }
        }
        
        // If no model ID found from element ID, use context
        if (!targetModel) {
          targetModel = ctx.modelId || "2106935752"; // Fallback to 2026 only if no model is set
        }
      }
      
      // CRITICAL: Only include elementId if it's explicitly provided in params.meaId
      // If params.meaId is undefined, don't include any element ID (even if ctx.elementId exists)
      // This ensures that when period changes, old element IDs are not included
      const pathParts = [
        tenant.symbol,
        targetModel,
        params.page || ctx.page || "",
        elementIdToUse, // Only use params.meaId, never fall back to ctx.elementId
      ].filter(part => part !== "" && part !== "undefined");
      const queryParts: string[] = [];
      if (!options?.noPeriod) {
        // Use period from params, then context period, then context originalPeriod
        const periodToUse = params.period || ctx.period || ctx.originalPeriod;
        if (periodToUse) {
          queryParts.push(`period=${encodeURIComponent(periodToUse)}`);
        }
      }
      if (params.wf) queryParts.push(`wf=${params.wf}`);
      if (params.type) queryParts.push(`type=${params.type}`);
      const getQueryPart = () => {
        if (queryParts.length > 0) {
          return `?${queryParts.join("&")}`;
        }
        return "";
      };
      const URL = `/${pathParts.join("/")}${getQueryPart()}`;
      if (params.replace) history.replace(URL);
      else history.push(URL);
    },
    [ctx.rootUrl, ctx.elementId, ctx.page, ctx.originalPeriod, ctx.period, ctx.modelId, tenant.symbol, history]
  );
  return cb;
};
export default useLink;
